/**
 * Public route for anonymous custom DIY project creation.
 * Users describe a project in free text → AI generates a step-by-step plan.
 * No auth required. Rate-limited to 2 plans per session client-side,
 * plus server-side daily limits via anonymousCreateCustomProject.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  Accordion,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Group,
  List,
  NativeSelect,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core'
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconClock,
  IconCurrencyDollar,
  IconDeviceFloppy,
  IconSparkles,
  IconTool,
  IconWand,
} from '@tabler/icons-react'
import { useAction } from 'convex/react'
import { api } from 'convex/_generated/api'
import { usePostHog } from '~/lib/posthog'
import { seo } from '~/utils/seo'

// =============================================================================
// CONSTANTS
// =============================================================================

const ANON_CUSTOM_COUNT_KEY = 'diy_anon_custom_count'
const ANON_CUSTOM_SESSION_KEY = 'diy_anon_custom_session'
const ANON_CUSTOM_LIMIT = 2

const SKILL_OPTIONS = [
  { value: 'beginner', label: 'Beginner — first time doing this type of project' },
  { value: 'intermediate', label: 'Intermediate — done a few similar projects' },
  { value: 'advanced', label: 'Advanced — comfortable with most DIY tasks' },
]

const BUDGET_OPTIONS = [
  { value: '', label: 'No budget in mind' },
  { value: 'under $100', label: 'Under $100' },
  { value: '$100–$500', label: '$100–$500' },
  { value: '$500–$1,500', label: '$500–$1,500' },
  { value: 'over $1,500', label: 'Over $1,500' },
]

// =============================================================================
// ROUTE
// =============================================================================

export const Route = createFileRoute('/diy/create')({
  head: () => ({
    meta: seo({
      title: 'Create a Custom DIY Project Plan | Anchor',
      description:
        'Describe any home improvement project and get a free step-by-step plan with tool lists, materials, and cost estimates. No account required.',
    }),
  }),
  component: DiyCreatePage,
})

// =============================================================================
// TYPES
// =============================================================================

interface ProjectStep {
  title: string
  description: string
  estimatedMinutes?: number
  tools: Array<string>
  materials: Array<string>
  tips?: Array<string>
  warnings?: Array<string>
}

interface GeneratedPlan {
  title: string
  estimatedHours: number
  estimatedCostMin: number
  estimatedCostMax: number
  difficulty: string
  steps: Array<ProjectStep>
  toolsSummary: { all: Array<string> }
}

// =============================================================================
// STORAGE HELPERS
// =============================================================================

function readCustomCount(): number {
  if (typeof localStorage === 'undefined') return 0
  try {
    const raw = localStorage.getItem(ANON_CUSTOM_COUNT_KEY)
    return raw ? Math.max(0, parseInt(raw, 10) || 0) : 0
  } catch { return 0 }
}

function incrementCustomCount(): void {
  if (typeof localStorage === 'undefined') return
  try {
    const next = Math.min(ANON_CUSTOM_LIMIT, readCustomCount() + 1)
    localStorage.setItem(ANON_CUSTOM_COUNT_KEY, String(next))
  } catch { /* ignore */ }
}

function readSessionToken(): string | undefined {
  if (typeof localStorage === 'undefined') return undefined
  try { return localStorage.getItem(ANON_CUSTOM_SESSION_KEY) ?? undefined } catch { return undefined }
}

function saveSessionToken(token: string): void {
  if (typeof localStorage === 'undefined') return
  try { localStorage.setItem(ANON_CUSTOM_SESSION_KEY, token) } catch { /* ignore */ }
}

async function getFingerprint(): Promise<string> {
  const components = [
    navigator.userAgent,
    screen.width.toString(),
    screen.height.toString(),
    screen.colorDepth.toString(),
    new Date().getTimezoneOffset().toString(),
    navigator.language,
    (typeof navigator.hardwareConcurrency === 'number' ? navigator.hardwareConcurrency : 'unknown').toString(),
  ]
  const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(components.join('|')))
  return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('')
}

// =============================================================================
// PAGE COMPONENT
// =============================================================================

function DiyCreatePage() {
  const posthog = usePostHog()
  const createCustomProject = useAction(api.ai.anonymousAgent.anonymousCreateCustomProject)

  const [description, setDescription] = useState('')
  const [skillLevel, setSkillLevel] = useState('intermediate')
  const [budgetRange, setBudgetRange] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [plan, setPlan] = useState<GeneratedPlan | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [customCount, setCustomCount] = useState(0)

  useEffect(() => {
    setCustomCount(readCustomCount())
  }, [])

  const atLimit = customCount >= ANON_CUSTOM_LIMIT

  const handleGenerate = useCallback(async () => {
    if (!description.trim() || isGenerating || atLimit) return
    setIsGenerating(true)
    setError(null)
    setPlan(null)

    posthog?.capture('custom_project_generate_started', { skillLevel, hasBudget: !!budgetRange })

    try {
      const fingerprint = await getFingerprint()
      const sessionToken = readSessionToken()

      const result = await createCustomProject({
        description: description.trim(),
        skillLevel,
        budgetRange: budgetRange || undefined,
        sessionToken,
        fingerprint,
      })

      if (result.sessionToken) saveSessionToken(result.sessionToken)

      if (result.success && result.plan) {
        setPlan(result.plan as GeneratedPlan)
        incrementCustomCount()
        setCustomCount(readCustomCount())
        posthog?.capture('custom_project_plan_generated', { skillLevel, stepsCount: result.plan.steps.length })
      } else {
        setError(result.error ?? 'Unable to generate plan. Please try again.')
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setIsGenerating(false)
    }
  }, [description, skillLevel, budgetRange, isGenerating, atLimit, createCustomProject, posthog])

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        {/* Back link */}
        <Box>
          <Button
            component={Link}
            to="/diy"
            variant="subtle"
            color="gray"
            size="sm"
            leftSection={<IconArrowLeft size={14} />}
          >
            Browse templates
          </Button>
        </Box>

        {/* Header */}
        <Box ta="center">
          <Group justify="center" gap="sm" mb="xs">
            <ThemeIcon size="lg" variant="light" color="primary">
              <IconWand size={20} />
            </ThemeIcon>
            <Title order={1} size="h2">Create a Custom Project Plan</Title>
          </Group>
          <Text c="dimmed" maw={540} mx="auto">
            Describe your project and we'll generate a step-by-step plan with tool lists,
            materials, and cost estimates — no account required.
          </Text>
        </Box>

        {!plan ? (
          /* ── FORM ── */
          <Paper withBorder p="xl" radius="md">
            <Stack gap="lg">
              <Stack gap="xs">
                <Text fw={500} size="sm">Describe your project <Text component="span" c="red">*</Text></Text>
                <Textarea
                  placeholder="e.g. Replace the bathroom vanity light fixture with a new LED bar light. The current fixture has two bulbs and I need to match the existing wiring."
                  minRows={3}
                  maxRows={6}
                  autosize
                  value={description}
                  onChange={(e) => setDescription(e.currentTarget.value)}
                  disabled={isGenerating || atLimit}
                />
                <Text size="xs" c="dimmed">{description.length}/1000 characters</Text>
              </Stack>

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Stack gap="xs">
                  <Text fw={500} size="sm">Your skill level</Text>
                  <NativeSelect
                    data={SKILL_OPTIONS}
                    value={skillLevel}
                    onChange={(e) => setSkillLevel(e.currentTarget.value)}
                    disabled={isGenerating || atLimit}
                  />
                </Stack>
                <Stack gap="xs">
                  <Text fw={500} size="sm">Budget range (optional)</Text>
                  <NativeSelect
                    data={BUDGET_OPTIONS}
                    value={budgetRange}
                    onChange={(e) => setBudgetRange(e.currentTarget.value)}
                    disabled={isGenerating || atLimit}
                  />
                </Stack>
              </SimpleGrid>

              {error && (
                <Text c="red" size="sm">{error}</Text>
              )}

              {atLimit ? (
                <Paper p="md" withBorder radius="md" bg="light-dark(var(--mantine-color-yellow-0), var(--mantine-color-dark-7))">
                  <Stack gap="xs" align="center">
                    <Text fw={500} ta="center">You've used your {ANON_CUSTOM_LIMIT} free custom plans</Text>
                    <Text size="sm" c="dimmed" ta="center">Sign up free to create unlimited custom projects and save your work.</Text>
                    <Button
                      component={Link}
                      to="/sign-up"
                      rightSection={<IconArrowRight size={16} />}
                    >
                      Sign Up Free
                    </Button>
                  </Stack>
                </Paper>
              ) : (
                <Group justify="space-between" align="center">
                  <Text size="xs" c="dimmed">
                    {ANON_CUSTOM_LIMIT - customCount} of {ANON_CUSTOM_LIMIT} free plans remaining
                  </Text>
                  <Button
                    leftSection={<IconSparkles size={16} />}
                    onClick={handleGenerate}
                    loading={isGenerating}
                    disabled={description.trim().length < 10}
                    size="md"
                  >
                    {isGenerating ? 'Generating your plan…' : 'Generate My Plan'}
                  </Button>
                </Group>
              )}
            </Stack>
          </Paper>
        ) : (
          /* ── PLAN RESULT ── */
          <Stack gap="lg">
            {/* Plan header */}
            <Paper withBorder p="lg" radius="md">
              <Stack gap="sm">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Title order={2} size="h3">{plan.title}</Title>
                  <Badge
                    variant="light"
                    color={plan.difficulty === 'beginner' ? 'green' : plan.difficulty === 'advanced' ? 'red' : 'yellow'}
                    size="md"
                  >
                    {plan.difficulty}
                  </Badge>
                </Group>

                <Group gap="lg" wrap="wrap">
                  <Group gap={6}>
                    <IconClock size={16} color="var(--mantine-color-dimmed)" />
                    <Text size="sm" c="dimmed">
                      ~{plan.estimatedHours} {plan.estimatedHours === 1 ? 'hour' : 'hours'}
                    </Text>
                  </Group>
                  <Group gap={6}>
                    <IconCurrencyDollar size={16} color="var(--mantine-color-dimmed)" />
                    <Text size="sm" c="dimmed">
                      ${plan.estimatedCostMin}–${plan.estimatedCostMax}
                    </Text>
                  </Group>
                  <Group gap={6}>
                    <IconTool size={16} color="var(--mantine-color-dimmed)" />
                    <Text size="sm" c="dimmed">
                      {plan.toolsSummary.all.length} tools needed
                    </Text>
                  </Group>
                </Group>
              </Stack>
            </Paper>

            {/* Tools summary */}
            {plan.toolsSummary.all.length > 0 && (
              <Paper withBorder p="md" radius="md">
                <Stack gap="xs">
                  <Text fw={600} size="sm">Tools you'll need</Text>
                  <Group gap="xs" wrap="wrap">
                    {plan.toolsSummary.all.map((tool) => (
                      <Badge key={tool} variant="outline" size="sm" color="gray">{tool}</Badge>
                    ))}
                  </Group>
                </Stack>
              </Paper>
            )}

            {/* Steps */}
            <Stack gap="xs">
              <Title order={3} size="h4">Step-by-Step Plan ({plan.steps.length} steps)</Title>
              <Accordion variant="separated" radius="md">
                {plan.steps.map((step, i) => (
                  <Accordion.Item key={i} value={String(i)}>
                    <Accordion.Control>
                      <Group gap="sm">
                        <ThemeIcon size="sm" variant="filled" color="primary" radius="xl">
                          <Text size="xs" fw={700}>{i + 1}</Text>
                        </ThemeIcon>
                        <Box>
                          <Text fw={500}>{step.title}</Text>
                          {step.estimatedMinutes && (
                            <Text size="xs" c="dimmed">{step.estimatedMinutes} min</Text>
                          )}
                        </Box>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="md" py="xs">
                        <Text size="sm">{step.description}</Text>

                        {step.warnings && step.warnings.length > 0 && (
                          <Paper p="sm" radius="sm" bg="light-dark(var(--mantine-color-red-0), var(--mantine-color-dark-8))">
                            <Stack gap={4}>
                              {step.warnings.map((w, wi) => (
                                <Text key={wi} size="xs" c="red" fw={500}>⚠ {w}</Text>
                              ))}
                            </Stack>
                          </Paper>
                        )}

                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                          {step.tools.length > 0 && (
                            <Stack gap={4}>
                              <Text size="xs" fw={600} tt="uppercase" c="dimmed">Tools</Text>
                              {step.tools.map((t) => (
                                <Text key={t} size="sm">• {t}</Text>
                              ))}
                            </Stack>
                          )}
                          {step.materials.length > 0 && (
                            <Stack gap={4}>
                              <Text size="xs" fw={600} tt="uppercase" c="dimmed">Materials</Text>
                              {step.materials.map((m) => (
                                <Text key={m} size="sm">• {m}</Text>
                              ))}
                            </Stack>
                          )}
                        </SimpleGrid>

                        {step.tips && step.tips.length > 0 && (
                          <Stack gap={4}>
                            <Text size="xs" fw={600} tt="uppercase" c="dimmed">Tips</Text>
                            {step.tips.map((tip, ti) => (
                              <Text key={ti} size="sm" c="dimmed">💡 {tip}</Text>
                            ))}
                          </Stack>
                        )}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Stack>

            <Divider />

            {/* Save CTA */}
            <Paper
              p="xl"
              withBorder
              radius="md"
              bg="light-dark(var(--mantine-color-primary-0), var(--mantine-color-dark-7))"
            >
              <Stack gap="lg" align="center">
                <ThemeIcon size="xl" variant="light" color="primary" radius="xl">
                  <IconDeviceFloppy size={24} />
                </ThemeIcon>
                <Title order={3} ta="center">Save & Track This Project</Title>
                <Text size="sm" c="dimmed" ta="center" maw={400}>
                  Create a free account to save this plan, check off steps as you go,
                  and get AI help when you get stuck.
                </Text>
                <List
                  spacing="xs"
                  size="sm"
                  center
                  icon={
                    <ThemeIcon color="green" size="sm" radius="xl">
                      <IconCheck size={12} />
                    </ThemeIcon>
                  }
                >
                  <List.Item>Save unlimited custom projects</List.Item>
                  <List.Item>Track progress step-by-step</List.Item>
                  <List.Item>Get AI help when you're stuck</List.Item>
                  <List.Item>Build a maintenance history for your home</List.Item>
                </List>
                <Button
                  size="lg"
                  component={Link}
                  to="/sign-up"
                  rightSection={<IconArrowRight size={18} />}
                  onClick={() => posthog?.capture('custom_project_save_cta_clicked')}
                >
                  Sign Up Free — No CC Required
                </Button>
              </Stack>
            </Paper>

            {/* Generate another */}
            {!atLimit && (
              <Group justify="center">
                <Button
                  variant="subtle"
                  onClick={() => { setPlan(null); setDescription(''); setError(null) }}
                >
                  ← Create another project
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  )
}
